// テキスト / 付箋の本文中に含まれる URL を検出し、リンク化のためのセグメントへ分割する純粋ユーティリティ。
//
// セキュリティ方針: リンク化・遷移の対象は http / https スキームのみに限定する。javascript: / data: 等は
// URL 検出の正規表現（https?:// 始まり）で一次的に弾き、isHttpUrl で二次的に検証する多層防御とする。

/** 文字列が http / https の URL かどうか（リンク化・遷移の許可判定）。 */
export function isHttpUrl(url: string): boolean {
  if (typeof url !== "string" || url.length === 0 || url.length > 8192) return false;
  try {
    const protocol = new URL(url).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export interface LinkSegment {
  readonly type: "text" | "link";
  readonly value: string;
  /** type==="link" のときのみ設定される、遷移先 URL（検証済み）。 */
  readonly url?: string | undefined;
  /**
   * このセグメントが消費した元テキスト（省略時は value と同じ）。
   * 表示テキスト付きリンク（[テキスト](URL)）では value=表示テキスト、raw=元の記法全体となり、
   * findUrlTokenAt の文字オフセット計算は raw 基準で行う。
   */
  readonly raw?: string | undefined;
}

/**
 * 外部リンク判定: URL のオリジンが現在のページ（selfOrigin）と異なるものを外部とみなす。
 * 同一オリジン（アプリ自身のキャンバス URL 等）は内部リンクとして同一タブで開く。
 * URL の解釈に失敗した場合は安全側（外部扱い = 別タブ + noopener）へ倒す。
 */
export function isExternalHttpUrl(
  url: string,
  selfOrigin: string = typeof window === "undefined" ? "" : window.location.origin,
): boolean {
  try {
    return new URL(url).origin !== selfOrigin;
  } catch {
    return true;
  }
}

// 表示テキスト付きリンク（Markdown 形式 [テキスト](URL)）の開始部分（[テキスト]( まで）を
// 検出する正規表現。リンク付き HTML の貼り付け（use-object-text-editor の onPaste）で保持した
// 表示テキストを非編集表示でリンクとして描画するために使う。
// URL の終端は正規表現の貪欲一致に任せると、同一行の後続 )（隣接リンクや (メモ) 等）まで
// 飲み込んでしまうため、findMarkdownLinkUrlEnd が「括弧深度 0 で閉じる )」を走査して確定する。
// これにより URL 内で均衡した括弧（例: /wiki/Function_(mathematics)）は許容しつつ、
// リンク外側の文字を URL に取り込まない。
const MARKDOWN_LINK_START_REGEX = /\[((?:\\[\\[\]]|[^\]\\\n])+)\]\(/gu;

/**
 * "](" の直後（start）から走査し、URL の終端となる閉じ括弧の位置を返す。
 * URL 内で開いた ( は深度で対応させ、深度 0 で現れた ) を delimiter とする。
 * 空白・改行に出会った場合は Markdown リンクとして成立しないため null を返す。
 */
function findMarkdownLinkUrlEnd(text: string, start: number): number | null {
  let depth = 0;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i]!;
    if (ch === "(") {
      depth += 1;
      continue;
    }
    if (ch === ")") {
      if (depth === 0) {
        return i;
      }
      depth -= 1;
      continue;
    }
    if (/\s/u.test(ch)) {
      return null;
    }
  }
  return null;
}

/** [テキスト](URL) のテキスト側エスケープ（\[ \] \\）を元の文字へ戻す */
function unescapeLinkLabel(label: string): string {
  return label.replace(/\\([\\[\]])/gu, "$1");
}

// http(s):// で始まり、URL 本体として扱う文字の連続を URL 候補とみなす。末尾の約物・不均衡な
// 閉じ括弧は trimUrlTrailing で URL から切り離す。
// 「URL 本体として扱わない境界文字」＝空白に加え、日本語の助詞・約物（ひらがな ぀-ゟ /
// カタカナ ゠-ヿ / CJK 記号・句読点 　-〿 / 全角・半角形 ＀-￯）を除外する。
// これにより空白の無い日本語文（例: "詳細はhttps://example.comです"）で "です" まで取り込む過剰検出を防ぐ。
// CJK 統合漢字（一-鿿）は日本語パスの URL（例: /wiki/日本語）を壊さないよう URL 本体として許可する。
const URL_REGEX = /https?:\/\/[^\s　-〿぀-ゟ゠-ヿ＀-￯]+/giu;

// URL 末尾に紛れ込みやすい句読点（この集合は無条件で URL から切り離す）。
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", "、", "。", "，", "．"]);
// 閉じ括弧・引用符と、その対応する開き括弧。URL 内に対応する開きが無い（不均衡な）場合のみ切り離す。
const CLOSERS: Readonly<Record<string, string>> = {
  ")": "(",
  "]": "[",
  "}": "{",
  "）": "（",
  "」": "「",
  "』": "『",
  "”": "“",
  "’": "‘",
};

const OPENERS = new Set(Object.values(CLOSERS));

/**
 * URL 候補の末尾に紛れ込んだ約物・閉じ括弧を切り離し、URL 本体と末尾テキストへ分ける。
 * 例: "https://example.com/foo)." → { url: "https://example.com/foo", trailing: ")." }
 * ただし "https://ja.wikipedia.org/wiki/Foo_(bar)" のように URL 内で括弧が均衡していれば残す。
 *
 * 空白の無い日本語文（例: "参考(https://example.com/x)です"）でも URL を過剰に取り込まないよう、
 * 左から走査して「対応する開き括弧の無い閉じ括弧」が現れた位置で URL を打ち切る。その後、末尾の
 * 句読点を剥がす。
 */
function trimUrlTrailing(raw: string): { url: string; trailing: string } {
  const stack: string[] = [];
  let cut = raw.length;
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i]!;
    if (OPENERS.has(ch)) {
      stack.push(ch);
      continue;
    }
    const opener = CLOSERS[ch];
    if (opener !== undefined) {
      if (stack.length > 0 && stack[stack.length - 1] === opener) {
        stack.pop();
      } else {
        // 対応する開きの無い閉じ括弧 → ここより後ろは URL に含めない。
        cut = i;
        break;
      }
    }
  }
  let end = cut;
  while (end > 0 && TRAILING_PUNCTUATION.has(raw[end - 1]!)) {
    end -= 1;
  }
  return { url: raw.slice(0, end), trailing: raw.slice(end) };
}

/**
 * index 位置（Markdown 開始候補の "[" の位置）が、直前から続く素の URL トークンの途中か。
 * 直前の空白までさかのぼった接頭辞の中で「index まで連続する最後の http(s):// 開始位置」を
 * 探し、そこからの部分文字列が有効な URL であれば、この "[" は bare URL の path / query の
 * 一部とみなして Markdown リンクとして先取りしない
 *（例: https://example.com/docs/[guide](draft) を途中で切断しない）。
 * 接頭辞の先頭一致ではなく最後の http(s):// を探すのは、「詳細はhttps://...」のように
 * 空白を挟まず URL が始まるケースや、「参考(https://...」のように括弧に続くケースでも
 * bare URL 内と判定できるようにするため。
 */
// isInsideBareUrlPrefix 用: 素の URL 本体（URL_REGEX と同じ文字集合）の先頭一致判定。
const BARE_URL_PREFIX_REGEX = /^https?:\/\/[^\s　-〿぀-ゟ゠-ヿ＀-￯]+/iu;

function isInsideBareUrlPrefix(text: string, index: number): boolean {
  let tokenStart = index;
  while (tokenStart > 0 && !/\s/u.test(text[tokenStart - 1]!)) {
    tokenStart -= 1;
  }
  const prefix = text.slice(tokenStart, index);
  const httpStart = Math.max(prefix.lastIndexOf("http://"), prefix.lastIndexOf("https://"));
  if (httpStart < 0) {
    return false;
  }
  // URL 本体が index の直前まで途切れず連続していることを確認する。途中で URL が
  // 終わっている場合（隣接する Markdown リンクの ")" の後や、CJK 文字・末尾約物で
  // 打ち切られる場合）は bare URL の内側ではないため、Markdown 判定を妨げない。
  const candidate = prefix.slice(httpStart);
  const body = BARE_URL_PREFIX_REGEX.exec(candidate);
  if (body === null || body[0].length !== candidate.length) {
    return false;
  }
  const { url } = trimUrlTrailing(candidate);
  if (url.length !== candidate.length) {
    return false;
  }
  return isHttpUrl(url);
}

/** 素の URL 検出のみでテキストをセグメント列へ分割する（従来の分割処理） */
function splitBareUrlSegments(text: string): readonly LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  URL_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    if (start > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, start) });
    }
    const { url, trailing } = trimUrlTrailing(raw);
    if (isHttpUrl(url)) {
      segments.push({ type: "link", value: url, url });
    } else {
      segments.push({ type: "text", value: url });
    }
    if (trailing.length > 0) {
      segments.push({ type: "text", value: trailing });
    }
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
}

/**
 * 本文全体（改行含む）を、通常テキストと URL リンクのセグメント列へ分割する。
 * まず表示テキスト付きリンク（[テキスト](URL)）を抽出し、残りのテキストへ素 URL 検出を
 * 適用する（リンク内 URL の二重リンク化を防ぐ）。
 * URL でも http/https でなければ text セグメントへ落とす（安全側）。
 */
export function splitTextIntoLinkSegments(text: string): readonly LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  MARKDOWN_LINK_START_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKDOWN_LINK_START_REGEX.exec(text)) !== null) {
    if (isInsideBareUrlPrefix(text, match.index)) {
      // 素の URL トークンの途中に現れた "[...](" は Markdown として先取りせず、
      // 後段の splitBareUrlSegments（素 URL 検出）に 1 本の URL として任せる。
      continue;
    }
    const start = match.index;
    if (start < lastIndex) {
      // 直前に確定したリンク範囲の内側での誤マッチ（URL 内の "[x](" 等）は読み飛ばす。
      continue;
    }
    const urlStart = MARKDOWN_LINK_START_REGEX.lastIndex;
    const urlEnd = findMarkdownLinkUrlEnd(text, urlStart);
    if (urlEnd === null) {
      // URL 終端（深度 0 の )）が確定しない場合は Markdown リンクとして扱わず、
      // この開始位置は素のテキスト（後段の素 URL 検出）に任せる。
      continue;
    }
    const raw = text.slice(start, urlEnd + 1);
    const label = unescapeLinkLabel(match[1] ?? "");
    // 記法内の URL は括弧で明示的に区切られているため、素 URL 検出と異なり末尾約物の
    // 切り離し（trimUrlTrailing）は行わず、括弧内をそのまま URL として尊重する
    //（Markdown の一般挙動と一致。raw = 記法全体となり原文との連続オフセット対応も保たれる）。
    const url = text.slice(urlStart, urlEnd);
    if (start > lastIndex) {
      segments.push(...splitBareUrlSegments(text.slice(lastIndex, start)));
    }
    if (isHttpUrl(url)) {
      segments.push({ type: "link", value: label, url, raw });
    } else {
      // http(s) でない URL はリンク化せず、記法全体をテキストのまま残す（安全側。
      // 素 URL 検出へも戻さないため、href 内の http(s) 断片が部分リンク化されることもない）。
      segments.push({ type: "text", value: raw });
    }
    lastIndex = start + raw.length;
    // URL 内の "[x](" などを次の開始位置として拾わないよう、走査位置をリンク終端へ進める。
    MARKDOWN_LINK_START_REGEX.lastIndex = lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push(...splitBareUrlSegments(text.slice(lastIndex)));
  }
  return coalesceTextSegments(segments);
}

// 連続する text セグメントを 1 つに結合する（URL 末尾トリムと URL 後テキストが別セグメントになるのを畳む）。
function coalesceTextSegments(segments: readonly LinkSegment[]): readonly LinkSegment[] {
  const merged: LinkSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (segment.type === "text" && last && last.type === "text") {
      merged[merged.length - 1] = { type: "text", value: last.value + segment.value };
    } else {
      merged.push(segment);
    }
  }
  return merged;
}

/**
 * 全文中の文字オフセット位置に重なる URL（リンクセグメント）を返す。無ければ null。
 * 編集中（contentEditable）に Ctrl/Cmd+クリックした位置の URL を判定するのに使う。
 * オフセットは編集中の元テキスト基準のため、表示テキスト付きリンクは raw（記法全体）の長さで数える。
 */
export function findUrlTokenAt(text: string, offset: number): string | null {
  if (offset < 0 || offset > text.length) return null;
  let pos = 0;
  const segments = splitTextIntoLinkSegments(text);
  for (const [index, segment] of segments.entries()) {
    const segEnd = pos + (segment.raw ?? segment.value).length;
    // 終端は inclusive（リンク末尾の直後のキャレットでも開ける）としつつ、直後に別リンクが
    // 隣接する場合の共有境界だけは後ろ側のリンクへ譲る（前側リンクが境界を吸うと、
    // [A](url)[B](url) の 2 本目先頭を Ctrl+クリックしたとき A が開いてしまう）。
    const sharedBoundaryWithNextLink =
      segments[index + 1]?.type === "link" && offset === segEnd;
    if (
      segment.type === "link" &&
      segment.url &&
      offset >= pos &&
      offset <= segEnd &&
      !sharedBoundaryWithNextLink
    ) {
      return segment.url;
    }
    pos = segEnd;
  }
  return null;
}
